import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Library, Loader2, Ban, Search, Archive as ArchiveIcon, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { OrgTabs } from "@/components/OrgTabs";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useOrg } from "@/hooks/useOrg";
import { kindBadge } from "@/lib/kinds";
import { api, apiErr } from "@/lib/api";

export default function OrgCatalog() {
  const { slug } = useParams();
  const { org } = useOrg(slug);
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const isAdmin = org && (org.role === "owner" || org.role === "admin");
  // ponytail: client-side filter — the catalog endpoint returns full metadata (≤1000 rows).
  const matches = (s) =>
    !q.trim() ||
    [s.name, s.slug, s.short_description, s.category, s.creator_name, kindBadge(s.kind) || s.kind]
      .join(" ").toLowerCase().includes(q.trim().toLowerCase());
  const shown = (items || []).filter((s) => !s.archived && matches(s));
  const archived = (items || []).filter((s) => s.archived && matches(s));

  const load = useCallback(() => {
    if (!slug) return;
    api.get(`/orgs/${slug}/catalog`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  const yank = async (e, s) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Pull v${s.published_version} of ${s.slug} from the registry?`)) return;
    try {
      await api.post(`/orgs/${slug}/versions/${s.published_version_id}/yank`, { note: "yanked from org catalog" });
      toast.success(`Pulled ${s.slug} v${s.published_version} — members' next sync removes it`);
      load();
    } catch (err) {
      toast.error(apiErr(err, "Could not yank"));
    }
  };

  // Archive rights mirror the API: the creator, or an org admin.
  const canArchive = (s) => isAdmin || (user && s.creator_id === user.user_id);
  const setArchived = async (e, s, archiving) => {
    e.preventDefault();
    e.stopPropagation();
    if (archiving && !window.confirm(`Archive ${s.slug}? Members' next sync removes it. Nothing is deleted.`)) return;
    try {
      await api.post(`/skills/${s.id}/${archiving ? "archive" : "unarchive"}`);
      toast.success(archiving ? `Archived ${s.slug}` : `Unarchived ${s.slug} — republish a version to bring it back`);
      load();
    } catch (err) {
      toast.error(apiErr(err, "Action failed"));
    }
  };

  return (
    <AppShell>
      <OrgTabs slug={slug} />
      <div className="flex items-center gap-2">
        <Library className="h-5 w-5 text-[color:var(--rb-accent)]" />
        <h1 className="rb-display text-2xl font-semibold tracking-tight text-[color:var(--rb-text)]">
          Catalog{org ? ` — ${org.name}` : ""}
        </h1>
      </div>
      <p className="mt-1 text-sm text-[color:var(--rb-text-faint)]">
        Everything built or uploaded for this org. Members get the full artifact — no locked teasers.
      </p>

      {items !== null && items.length > 0 && (
        <div className="relative mt-4 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--rb-text-faint)]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="org-catalog-search"
            placeholder="Search the catalog — name, description, kind, @creator…"
            className="border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] pl-9 text-sm text-[color:var(--rb-text)]" />
        </div>
      )}

      {items === null ? (
        <div className="mt-8 flex justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-[color:var(--rb-text-faint)]" />
        </div>
      ) : items.length === 0 ? (
        <div
          className="mt-8 rounded-2xl border border-dashed border-[color:var(--rb-border)] p-8 text-center text-sm text-[color:var(--rb-text-faint)]"
          data-testid="org-catalog-empty"
        >
          Nothing here yet —{" "}
          <Link to={`/build?org=${slug}`} className="text-[color:var(--rb-accent)] hover:underline">build</Link>{" "}
          or{" "}
          <Link to={`/upload?org=${slug}`} className="text-[color:var(--rb-accent)] hover:underline">upload</Link>{" "}
          the first artifact.
        </div>
      ) : shown.length === 0 && archived.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-dashed border-[color:var(--rb-border)] p-8 text-center text-sm text-[color:var(--rb-text-faint)]" data-testid="org-catalog-no-match">
          Nothing matches “{q.trim()}”.
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="org-catalog-grid">
          {shown.map((s) => (
            <Link key={s.id} to={`/orgs/${slug}/artifacts/${s.id}`} data-testid="org-catalog-item"
              className="block rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-4 hover:border-[color:var(--rb-accent)]">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-medium text-[color:var(--rb-text)]">{s.name}</span>
                {kindBadge(s.kind) && (
                  <span className="shrink-0 rounded-full border border-[color:var(--rb-border)] px-2 py-0.5 text-[10px] text-[color:var(--rb-text-faint)]">
                    {kindBadge(s.kind)}
                  </span>
                )}
              </div>
              <p className="mt-1 line-clamp-2 text-xs text-[color:var(--rb-text-faint)]">{s.short_description || "No description yet."}</p>
              <div className="mt-2 flex items-center justify-between gap-2">
                <p className="text-[10px] text-[color:var(--rb-text-faint)]">by @{s.creator_name}</p>
                <div className="flex items-center gap-1.5">
                  {s.published_version ? (
                    <span className="rounded-full bg-[color:rgba(45,212,191,0.14)] px-2 py-0.5 text-[10px] text-[color:var(--rb-accent)]">
                      Published v{s.published_version}
                    </span>
                  ) : null}
                  {s.has_pending_version ? (
                    <span className="rounded-full bg-[color:var(--rb-gold-soft)] px-2 py-0.5 text-[10px] text-[color:var(--rb-gold)]">Awaiting approval</span>
                  ) : null}
                  {!s.published_version && !s.has_pending_version ? (
                    <span className="rounded-full border border-[color:var(--rb-border)] px-2 py-0.5 text-[10px] text-[color:var(--rb-text-faint)]">Draft</span>
                  ) : null}
                  {isAdmin && s.published_version_id ? (
                    <button onClick={(e) => yank(e, s)} data-testid="org-catalog-yank-button" title="Pull this version from the registry"
                      className="text-[color:var(--rb-text-faint)] hover:text-[color:#f87171]">
                      <Ban className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                  {canArchive(s) ? (
                    <button onClick={(e) => setArchived(e, s, true)} data-testid="org-catalog-archive-button" title="Archive — retire from the catalog, never deleted"
                      className="text-[color:var(--rb-text-faint)] hover:text-[color:var(--rb-text)]">
                      <ArchiveIcon className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {items !== null && archived.length > 0 && (
        <div className="mt-6">
          <button onClick={() => setShowArchived((v) => !v)} data-testid="org-catalog-archived-toggle"
            className="flex items-center gap-1.5 text-xs text-[color:var(--rb-text-faint)] hover:text-[color:var(--rb-text)]">
            <ArchiveIcon className="h-3.5 w-3.5" /> Archived ({archived.length}) {showArchived ? "— hide" : "— show"}
          </button>
          {showArchived && (
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="org-catalog-archived-grid">
              {archived.map((s) => (
                <Link key={s.id} to={`/orgs/${slug}/artifacts/${s.id}`} data-testid="org-catalog-archived-item"
                  className="block rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-4 opacity-70 hover:border-[color:var(--rb-border-strong)] hover:opacity-100">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-medium text-[color:var(--rb-text)]">{s.name}</span>
                    <span className="shrink-0 rounded-full border border-[color:var(--rb-border)] px-2 py-0.5 text-[10px] text-[color:var(--rb-text-faint)]">Archived</span>
                  </div>
                  <p className="mt-1 line-clamp-2 text-xs text-[color:var(--rb-text-faint)]">{s.short_description || "No description yet."}</p>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <p className="text-[10px] text-[color:var(--rb-text-faint)]">by @{s.creator_name}</p>
                    {canArchive(s) ? (
                      <button onClick={(e) => setArchived(e, s, false)} data-testid="org-catalog-unarchive-button" title="Bring it back to the working set"
                        className="flex items-center gap-1 text-[10px] text-[color:var(--rb-text-faint)] hover:text-[color:var(--rb-text)]">
                        <ArchiveRestore className="h-3 w-3" /> Unarchive
                      </button>
                    ) : null}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </AppShell>
  );
}
